import React, { useState, useEffect, useRef, useCallback } from 'react';
import { collection, addDoc, updateDoc, doc, serverTimestamp, query, where, onSnapshot, getDocs, deleteField } from '../../lib/auditedFirestore';
import { ref, uploadBytes, getDownloadURL, uploadString } from 'firebase/storage';
import { db, storage } from '../../lib/firebase';
import Spinner from '../../components/Spinner';
import AdvancedWordProcessor from '../../components/editors/AdvancedWordProcessor';
import CustomSpreadsheet from '../../components/editors/CustomSpreadsheet';
import StarterSpreadsheetEditor from '../../components/editors/StarterSpreadsheetEditor';
import {validateStarterQuestions} from '../../utils/starterSpreadsheet';
import {EXAM_CATEGORIES, normalizeExamCategory, inferExamCategory, examCategoryLabel} from '../../utils/examCatalog';

const BankSelectorModal = ({ paperId, onImport, onClose, setModal }) => {
    const [bankQuestions, setBankQuestions] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    
    const [topics, setTopics] = useState([]);
    
    const [filterTopic, setFilterTopic] = useState('');
    const [filterSection, setFilterSection] = useState('');
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const q = query(collection(db, "topics"), where("paperId", "==", paperId));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            data.sort((a, b) => a.name.localeCompare(b.name));
            setTopics(data);
        });
        return () => unsubscribe();
    }, [paperId]);

    useEffect(() => {
        const q = query(collection(db, "questionBank"), where("paperId", "==", paperId));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setBankQuestions(data);
            setIsLoading(false);
        });
        return () => unsubscribe();
    }, [paperId]);

    const filteredQuestions = bankQuestions.filter(q => {
        const matchTopic = filterTopic ? q.topic === filterTopic : true;
        const matchSection = filterSection ? q.section === filterSection : true;
        return matchTopic && matchSection;
    });

    const toggleSelect = (id) => {
        setSelectedIds(prev => 
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        );
    };

    const handleImport = () => {
        const selectedQuestions = bankQuestions.filter(q => selectedIds.includes(q.id));
        onImport(selectedQuestions);
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-bold">Select Questions from Bank</h3>
                    <button onClick={onClose} className="text-2xl text-gray-500 hover:text-gray-800">&times;</button>
                </div>

                {/* Filters */}
                <div className="flex gap-4 mb-4">
                    <select 
                        value={filterTopic} 
                        onChange={(e) => setFilterTopic(e.target.value)} 
                        className="flex-1 p-2 border rounded"
                    >
                        <option value="">All Topics</option>
                        {topics.map(topic => (
                            <option key={topic.id} value={topic.name}>
                                {topic.name}
                            </option>
                        ))}
                    </select>

                    <select 
                        value={filterSection} 
                        onChange={(e) => setFilterSection(e.target.value)} 
                        className="w-48 p-2 border rounded"
                    >
                        <option value="">All Sections</option>
                        <option value="A">Section A</option>
                        <option value="B">Section B</option>
                        <option value="C">Section C</option>
                    </select>
                </div>

                {/* List */}
                <div className="flex-1 overflow-y-auto border rounded p-2 space-y-2 bg-gray-50">
                    {isLoading ? <Spinner color="border-purple-500" /> : filteredQuestions.length === 0 ? (
                        <p className="text-center text-gray-500 py-4">No questions found matching filters.</p>
                    ) : (
                        filteredQuestions.map(q => (
                            <div key={q.id} className={`p-3 border rounded flex gap-3 items-start cursor-pointer hover:bg-purple-50 ${selectedIds.includes(q.id) ? 'bg-purple-100 border-purple-400' : 'bg-white'}`} onClick={() => toggleSelect(q.id)}>
                                <input 
                                    type="checkbox" 
                                    checked={selectedIds.includes(q.id)} 
                                    onChange={() => toggleSelect(q.id)}
                                    className="mt-1.5 h-4 w-4 text-purple-600"
                                />
                                <div className="flex-1">
                                    <div className="flex gap-2 mb-1">
                                        <span className="text-xs font-bold bg-gray-200 px-2 py-0.5 rounded">{q.type}</span>
                                        <span className="text-xs font-bold bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">Sec {q.section}</span>
                                        <span className="text-xs font-semibold text-gray-600">{q.topic}</span>
                                        <span className="text-xs text-gray-500">({q.marks} Marks)</span>
                                    </div>
                                    <div 
                                        className="text-sm text-gray-800 line-clamp-2" 
                                        dangerouslySetInnerHTML={{ __html: q.text || '(Image Only Question)' }} 
                                    />
                                </div>
                            </div>
                        ))
                    )}
                </div>

                <div className="mt-4 flex justify-between items-center">
                    <span className="text-sm font-semibold text-purple-700">{selectedIds.length} questions selected</span>
                    <div className="flex gap-2">
                        <button onClick={onClose} className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400 text-gray-800">Cancel</button>
                        <button onClick={handleImport} disabled={selectedIds.length === 0} className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50">Import Selected</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const AutoGeneratorModal = ({ paperId, onGenerate, onClose, setModal }) => {
    const [config, setConfig] = useState({
        topicKeyword: '',
        countA: 0,
        countB: 0,
        countC: 0
    });
    const [isGenerating, setIsGenerating] = useState(false);

    const handleGenerate = async () => {
        setIsGenerating(true);
        try {
            const q = query(collection(db, "questionBank"), where("paperId", "==", paperId));
            const snapshot = await getDocs(q);
            const allQuestions = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            let pool = allQuestions;
            if (config.topicKeyword.trim()) {
                const keyword = config.topicKeyword.toLowerCase();
                pool = pool.filter(q => q.topic.toLowerCase().includes(keyword));
            }

            const poolA = pool.filter(q => q.section === 'A');
            const poolB = pool.filter(q => q.section === 'B');
            const poolC = pool.filter(q => q.section === 'C');

            if (poolA.length < config.countA) throw new Error(`Not enough Section A questions. Available: ${poolA.length}, Requested: ${config.countA}`);
            if (poolB.length < config.countB) throw new Error(`Not enough Section B questions. Available: ${poolB.length}, Requested: ${config.countB}`);
            if (poolC.length < config.countC) throw new Error(`Not enough Section C questions. Available: ${poolC.length}, Requested: ${config.countC}`);

            const shuffle = (array) => array.sort(() => 0.5 - Math.random());
            
            const selectedA = shuffle([...poolA]).slice(0, config.countA);
            const selectedB = shuffle([...poolB]).slice(0, config.countB);
            const selectedC = shuffle([...poolC]).slice(0, config.countC);

            const finalQuestions = [...selectedA, ...selectedB, ...selectedC];

            if (finalQuestions.length === 0) {
                throw new Error("No questions selected based on current criteria.");
            }

            onGenerate(finalQuestions);
            onClose();

        } catch (error) {
            setModal({ show: true, message: error.message });
        } finally {
            setIsGenerating(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-2xl max-w-md w-full">
                <h3 className="text-xl font-bold mb-4">Auto-Generate Exam</h3>
                
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-bold text-gray-700">Topic Filter (Optional)</label>
                        <input 
                            type="text" 
                            className="w-full p-2 border rounded" 
                            placeholder="e.g. Financial Reporting"
                            value={config.topicKeyword}
                            onChange={e => setConfig({...config, topicKeyword: e.target.value})}
                        />
                        <p className="text-xs text-gray-500">Leave blank to select from all topics.</p>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                        <div>
                            <label className="block text-xs font-bold text-gray-700">Sec A Qs</label>
                            <input 
                                type="number" 
                                className="w-full p-2 border rounded" 
                                min="0"
                                value={config.countA}
                                onChange={e => setConfig({...config, countA: parseInt(e.target.value) || 0})}
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-700">Sec B Qs</label>
                            <input 
                                type="number" 
                                className="w-full p-2 border rounded" 
                                min="0"
                                value={config.countB}
                                onChange={e => setConfig({...config, countB: parseInt(e.target.value) || 0})}
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-700">Sec C Qs</label>
                            <input 
                                type="number" 
                                className="w-full p-2 border rounded" 
                                min="0"
                                value={config.countC}
                                onChange={e => setConfig({...config, countC: parseInt(e.target.value) || 0})}
                            />
                        </div>
                    </div>
                </div>

                <div className="mt-6 flex justify-end gap-2">
                    <button onClick={onClose} className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400 text-gray-800">Cancel</button>
                    <button onClick={handleGenerate} disabled={isGenerating} className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded hover:from-purple-700 hover:to-pink-700">
                        {isGenerating ? 'Generating...' : 'Generate'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const CreateExam = ({ setModal, user, examToEdit, onFinishEditing, paper }) => {
    const [title, setTitle] = useState('');
    const [category, setCategory] = useState('auto');
    const [duration, setDuration] = useState(60);
    const [mode, setMode] = useState('on-demand');
    const [examId, setExamId] = useState(examToEdit?.id || null);
    const [saveStatus, setSaveStatus] = useState('idle');
    const isProfessional = paper && paper.level === 'professional';

    // -- VERSIONING & AUTO-SAVE STATE --
    const [versions, setVersions] = useState([]);
    const [activeVersionId, setActiveVersionId] = useState(null);
    const [isCreatingVersion, setIsCreatingVersion] = useState(false);
    const [autoSaves, setAutoSaves] = useState([]);

    // -- MODALS STATE --
    const [showBankSelector, setShowBankSelector] = useState(false);
    const [showAutoGenerator, setShowAutoGenerator] = useState(false);

    const defaultQuestion = {
        type: isProfessional ? 'descriptive' : 'mcq',
        text: '',
        images: [],
        options: ['', '', '', ''],
        correctAnswer: [],
        marks: isProfessional ? 10 : 1,
        explanation: '',
        explanationFormat: 'text',
        isProcessing: false,
        section: (paper.sections && paper.sections.length > 0) ? paper.sections[0].name : 'A',
        syllabusArea: '', 
        suggestedAnswerFormat: 'text', 
        suggestedAnswerContent: '', 
        starterSpreadsheet: null,
        suggestedAnswerExcel: null, 
        suggestedAnswerView: 'main', 
        markingSchemeFormat: 'text',
        markingSchemeContent: '',
        subQuestions: [] 
    };

    const [questions, setQuestions] = useState([defaultQuestion]);
    const [isLoading, setIsLoading] = useState(false);

    // Refs
    const dragItem = useRef();
    const dragOverItem = useRef();
    const dragQuestionItem = useRef();
    const dragOverQuestionItem = useRef();
    const isFirstRender = useRef(true);
    
    // -- BUGFIX: Locks & Instant Refs to prevent duplicate exam creation --
    const isFetchingDraft = useRef(true); 
    const autoSavesRef = useRef([]);
    const examIdRef = useRef(examToEdit?.id || null);
    const isCreatingExamRef = useRef(false);

    // Sync ref to state to avoid useEffect dependency loops during autosave
    useEffect(() => {
        autoSavesRef.current = autoSaves;
    }, [autoSaves]);

    const totalExamMarks = questions.reduce((sum, q) => sum + (Number(q.marks) || 0), 0);

    useEffect(() => {
        const loadExamData = async () => {
            isFetchingDraft.current = true; // Lock autosaves

            if (examToEdit) {
                setTitle(examToEdit.title || '');
                setCategory(normalizeExamCategory(examToEdit.category));
                setDuration(examToEdit.duration || 60);
                setMode(examToEdit.mode || 'on-demand');
                
                setExamId(examToEdit.id);
                examIdRef.current = examToEdit.id; // Sync instant ref
                
                setVersions(examToEdit.versions || []);
                setActiveVersionId(examToEdit.activeVersionId || null);
                setAutoSaves(examToEdit.autoSaves || []);

                let loadedQs = examToEdit.questions || [];
                
                if (examToEdit.questionsUrl) {
                    try {
                        const res = await fetch(examToEdit.questionsUrl);
                        if (!res.ok) {
                            throw new Error(`HTTP error! status: ${res.status}`);
                        }
                        const fetchedData = await res.json();
                        
                        if (Array.isArray(fetchedData)) {
                            loadedQs = fetchedData;
                        } else {
                            console.error("Fetched data is not an array:", fetchedData);
                        }
                    } catch (error) {
                        console.error("Failed to load questions from storage", error);
                    }
                }

                if (!Array.isArray(loadedQs)) {
                    loadedQs = [];
                }

                const questionsWithAllFields = loadedQs.map(q => ({
                    ...defaultQuestion,
                    ...q,
                    section: q.section || (paper.sections && paper.sections.length > 0 ? paper.sections[0].name : 'A'),
                    syllabusArea: q.syllabusArea || '',
                    suggestedAnswerExcel: q.suggestedAnswerExcel || null,
                    suggestedAnswerView: 'main' 
                }));
                setQuestions(questionsWithAllFields.length > 0 ? questionsWithAllFields : [defaultQuestion]);
            } else {
                setTitle('');
                setCategory('auto');
                setDuration(60);
                setMode('on-demand');
                setQuestions([defaultQuestion]);
                
                setExamId(null);
                examIdRef.current = null; // Sync instant ref
                
                setVersions([]);
                setActiveVersionId(null);
                setAutoSaves([]);
            }
            setSaveStatus('idle');
            isFetchingDraft.current = false; // Unlock autosaves
        };
        
        loadExamData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [examToEdit, paper]);

    const handleBankImport = (importedQuestions) => {
        const newQuestions = importedQuestions.map(q => {
            const { id, paperId, createdAt, ...rest } = q;
            return {
                ...defaultQuestion,
                ...rest,
                suggestedAnswerView: 'main',
                isProcessing: false
            };
        });

        setQuestions(prev => {
            if (prev.length === 1 && !prev[0].text && prev[0].images.length === 0) {
                return newQuestions;
            }
            return [...prev, ...newQuestions];
        });
        setModal({ show: true, message: `Successfully imported ${newQuestions.length} questions.` });
    };

    const handleAutoGenerate = (generatedQuestions) => {
        handleBankImport(generatedQuestions);
    };

    const handleCreateVersion = async () => {
        const starterErrors=validateStarterQuestions(questions);
        if(starterErrors.length){setModal({show:true,message:starterErrors.slice(0,5).join('\n')});return;}
        if (!title || !title.trim()) {
            setModal({ show: true, message: "Please enter an exam title before creating a version." });
            return;
        }

        setIsCreatingVersion(true);
        try {
            const savedExamId = await saveExamToFirestore(true);
            if (!savedExamId) throw new Error('Save the draft successfully before publishing a version.');

            const versionId = Date.now().toString();
            const versionNum = versions.length + 1;
            
            const questionsToSave = questions.map(({ isProcessing, suggestedAnswerView, ...rest }) => ({
                ...rest,
                marks: Number(rest.marks) || 0,
                section: rest.section || '',
                syllabusArea: rest.syllabusArea || ''
            }));
            
            const questionsJson = JSON.stringify(questionsToSave);
            const questionsStorageRef = ref(storage, `exam-questions/${savedExamId}_v${versionId}.json`);
            await uploadString(questionsStorageRef, questionsJson, 'raw', { contentType: 'application/json' });
            const vUrl = await getDownloadURL(questionsStorageRef);

            const newVersion = {
                id: versionId,
                versionNumber: versionNum,
                title,
                category,
                duration: Number(duration),
                mode,
                questionsUrl: vUrl,
                createdAt: new Date().toISOString()
            };

            const newVersionsArray = [...versions, newVersion];
            const updates = {
                versions: newVersionsArray
            };

            if (versions.length === 0) {
                updates.activeVersionId = versionId;
                updates.publishedQuestionsUrl = vUrl; 
                updates.publishedTitle = title;
                updates.publishedCategory = category;
                updates.publishedDuration = Number(duration);
                updates.publishedMode = mode;
                setActiveVersionId(versionId);
            }

            const examRef = doc(db, 'exams', savedExamId);
            await updateDoc(examRef, updates);
            
            setVersions(newVersionsArray);
            setModal({ show: true, message: `Version ${versionNum} successfully created and snapshotted!` });
            
        } catch (error) {
            console.error("Error creating version:", error);
            setModal({ show: true, message: `Failed to create version: ${error.message}` });
        } finally {
            setIsCreatingVersion(false);
        }
    };

    const handleSetActiveVersion = async (v) => {
        try {
            const examRef = doc(db, 'exams', examId);
            await updateDoc(examRef, {
                activeVersionId: v.id,
                publishedQuestionsUrl: v.questionsUrl,
                publishedTitle: v.title,
                publishedCategory: normalizeExamCategory(v.category),
                publishedDuration: v.duration,
                publishedMode: v.mode
            });
            setActiveVersionId(v.id);
            setModal({ show: true, message: `Version ${v.versionNumber} is now live and active for students.` });
        } catch (error) {
            console.error("Error setting active version:", error);
            setModal({ show: true, message: `Failed to set active version: ${error.message}` });
        }
    };

    const handleRestoreVersion = async (v, isAutoSave = false) => {
        const msg = isAutoSave 
            ? `Are you sure you want to restore the auto-save from ${new Date(v.timestamp).toLocaleString()}? Current unsaved changes will be lost.`
            : `Are you sure you want to restore Version ${v.versionNumber} into the editor? Any unsaved draft changes will be overwritten.`;
            
        if (!window.confirm(msg)) return;
        
        setIsLoading(true);
        try {
            const res = await fetch(v.url || v.questionsUrl);
            if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
            
            const fetchedData = await res.json();
            let loadedQs = Array.isArray(fetchedData) ? fetchedData : [];
            
            const questionsWithAllFields = loadedQs.map(q => ({
                ...defaultQuestion,
                ...q,
                section: q.section || (paper.sections && paper.sections.length > 0 ? paper.sections[0].name : 'A'),
                syllabusArea: q.syllabusArea || '',
                suggestedAnswerExcel: q.suggestedAnswerExcel || null,
                suggestedAnswerView: 'main' 
            }));

            setQuestions(questionsWithAllFields.length > 0 ? questionsWithAllFields : [defaultQuestion]);
            if (!isAutoSave) {
                setTitle(v.title || '');
                setCategory(normalizeExamCategory(v.category));
                setDuration(v.duration || 60);
                setMode(v.mode || 'on-demand');
            }
            
            if (isAutoSave && v.category !== undefined) setCategory(normalizeExamCategory(v.category));
            setSaveStatus('idle');
            setModal({ show: true, message: `Restored ${isAutoSave ? 'auto-save' : `Version ${v.versionNumber}`} to the editor.` });
        } catch (error) {
            console.error("Error restoring version:", error);
            setModal({ show: true, message: `Failed to restore: ${error.message}` });
        } finally {
            setIsLoading(false);
        }
    };

    const saveExamToFirestore = useCallback(async (silent = true) => {
        // Prevent saving if the initial data load hasn't finished yet
        if (isFetchingDraft.current) return;
        if (!title || !title.trim()) return; 

        // CRITICAL FIX: Use the ref to prevent stale closures and multiple doc creations
        let currentExamId = examIdRef.current;

        if (!currentExamId) {
            // Lock to prevent concurrent creates if the user is typing fast
            if (isCreatingExamRef.current) return; 
            
            isCreatingExamRef.current = true;
            setSaveStatus('saving');
            
            try {
                const docRef = await addDoc(collection(db, 'exams'), {
                    title: title || 'Untitled Exam',
                    category,
                    versions: [],
                    createdBy: user.uid,
                    createdAt: serverTimestamp(),
                    paperId: paper.id,
                });
                currentExamId = docRef.id;
                examIdRef.current = currentExamId; // Set immediately so next loops catch it
                setExamId(currentExamId);
            } catch (err) {
                console.error("Error creating document:", err);
                isCreatingExamRef.current = false;
                setSaveStatus('error');
                return;
            }
            isCreatingExamRef.current = false;
        }

        setSaveStatus('saving');
        try {
            const questionsToSave = questions.map(({ isProcessing, suggestedAnswerView, ...rest }) => ({
                ...rest,
                marks: Number(rest.marks) || 0,
                section: rest.section || '',
                syllabusArea: rest.syllabusArea || '' 
            }));

            // Create a unique auto-save timestamped JSON file
            const autoSaveId = Date.now().toString();
            const questionsJson = JSON.stringify(questionsToSave);
            const questionsStorageRef = ref(storage, `exam-questions/${currentExamId}_auto_${autoSaveId}.json`);
            await uploadString(questionsStorageRef, questionsJson, 'raw', { contentType: 'application/json' });
            const questionsUrl = await getDownloadURL(questionsStorageRef);

            // Maintain rolling 10 versions in array
            const newAutoSave = {
                id: autoSaveId,
                timestamp: new Date().toISOString(),
                category,
                url: questionsUrl
            };
            
            let updatedAutoSaves = [...autoSavesRef.current, newAutoSave];
            if (updatedAutoSaves.length > 10) {
                updatedAutoSaves = updatedAutoSaves.slice(updatedAutoSaves.length - 10);
            }
            setAutoSaves(updatedAutoSaves);

            const examData = {
                title,
                category,
                duration: Number(duration),
                questionsUrl: questionsUrl, 
                questions: deleteField(),
                questionCount: questionsToSave.length,
                autoSaves: updatedAutoSaves, // Save auto-save references to Firestore
                createdBy: user.uid,
                paperId: paper.id,
                paperLevel: paper.level || 'skill',
                mode: mode,
                updatedAt: serverTimestamp()
            };

            if (mode === 'invigilated') {
                examData.invigilationStatus = examToEdit?.invigilationStatus || 'pending';
            }

            const examRef = doc(db, 'exams', currentExamId);
            await updateDoc(examRef, examData);
            
            setSaveStatus('saved');
            if (!silent) setModal({ show: true, message: 'Draft saved successfully!' });
            return currentExamId;
            
        } catch (error) {
            console.error("Error saving exam draft:", error);
            setSaveStatus('error');
            if (!silent) setModal({ show: true, message: `Error saving: ${error.message}` });
        }
    }, [title, category, duration, questions, user.uid, paper.id, paper.level, mode, examToEdit, setModal]);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        
        // Prevent auto-save while fetching
        if (isFetchingDraft.current) return;

        const handler = setTimeout(() => {
            saveExamToFirestore(true);
        }, 1500);

        return () => {
            clearTimeout(handler);
        };
    }, [title, category, duration, mode, questions, saveExamToFirestore]);

    const handleQuestionChange = (index, field, value) => {
        setQuestions(previous=>previous.map((question,questionIndex)=>questionIndex===index?{...question,[field]:value}:question));
    };

    const handleOptionChange = (qIndex, oIndex, value) => {
        const newQuestions = [...questions];
        newQuestions[qIndex].options[oIndex] = value;
        setQuestions(newQuestions);
    };

    const handleMatchingChange = (qIndex, listType, itemIndex, value) => {
        const newQuestions = [...questions];
        newQuestions[qIndex][listType][itemIndex] = value;
        setQuestions(newQuestions);
    };

    const addMatchingItem = (qIndex, listType) => {
        const newQuestions = [...questions];
        newQuestions[qIndex][listType].push('');
        setQuestions(newQuestions);
    };

    const removeMatchingItem = (qIndex, listType, itemIndex) => {
        const newQuestions = [...questions];
        newQuestions[qIndex][listType].splice(itemIndex, 1);
        if (listType === 'options') {
            const newCorrectAnswer = { ...newQuestions[qIndex].correctAnswer };
            Object.keys(newCorrectAnswer).forEach(promptIdx => {
                if (newCorrectAnswer[promptIdx] === itemIndex) {
                    delete newCorrectAnswer[promptIdx];
                } else if (newCorrectAnswer[promptIdx] > itemIndex) {
                    newCorrectAnswer[promptIdx]--;
                }
            });
            newQuestions[qIndex].correctAnswer = newCorrectAnswer;
        }
        setQuestions(newQuestions);
    };

    const handleMatchingCorrectAnswerChange = (qIndex, promptIndex, optionIndexStr) => {
        const newQuestions = [...questions];
        const optionIndex = optionIndexStr === "" ? undefined : parseInt(optionIndexStr, 10);
        const newCorrectAnswer = { ...newQuestions[qIndex].correctAnswer };
        if (optionIndex === undefined) {
            delete newCorrectAnswer[promptIndex];
        } else {
            newCorrectAnswer[promptIndex] = optionIndex;
        }
        newQuestions[qIndex].correctAnswer = newCorrectAnswer;
        setQuestions(newQuestions);
    };

    const handleCorrectAnswerChange = (qIndex, oIndex) => {
        const newQuestions = [...questions];
        const question = newQuestions[qIndex];
        const currentCorrectAnswers = question.correctAnswer || [];

        if (currentCorrectAnswers.includes(oIndex)) {
            question.correctAnswer = currentCorrectAnswers.filter(i => i !== oIndex);
        } else {
            question.correctAnswer = [...currentCorrectAnswers, oIndex];
        }
        setQuestions(newQuestions);
    };
    
    const addQuestion = (type) => {
        let newQuestion = { ...defaultQuestion, type };
        if (type === 'mcq') {
            newQuestion = { ...newQuestion, marks: 1, correctAnswer: [] };
        } else if (type === 'descriptive') {
            newQuestion = { ...newQuestion, marks: 10 };
        } else if (type === 'dropdown') {
            newQuestion = { ...newQuestion, correctAnswer: 0 };
        } else if (type === 'mcq-grid') {
            newQuestion = { ...newQuestion, rows: ['Row 1'], columns: ['Column 1'], correctAnswer: {} };
        } else if (type === 'short-answer') {
            newQuestion = { ...newQuestion, correctAnswer: '', marks: 2 };
        } else if (type === 'matching') {
            newQuestion = { ...newQuestion, prompts: ['Prompt 1', 'Prompt 2'], options: ['Option A', 'Option B'], correctAnswer: {}, marks: 4 };
        }
        setQuestions([...questions, newQuestion]);
    };

    const handleQuestionReorder = () => {
        const newQuestions = [...questions];
        const draggedItemContent = newQuestions.splice(dragQuestionItem.current, 1)[0];
        newQuestions.splice(dragOverQuestionItem.current, 0, draggedItemContent);
        dragQuestionItem.current = null;
        dragOverQuestionItem.current = null;
        setQuestions(newQuestions);
    };

    const handleImageUpload = (event, qIndex, field, isMultiple) => {
        const files = Array.from(event.target.files);
        if (files.length === 0) return;
    
        handleQuestionChange(qIndex, 'isProcessing', true);
    
        const uploadPromises = files.map(file => {
            const storagePath = field === 'images' ? 'exam-images' : 'descriptive-assets';
            const storageRef = ref(storage, `${storagePath}/${Date.now()}-${file.name}`);
            return uploadBytes(storageRef, file).then(snapshot => getDownloadURL(snapshot.ref));
        });
    
        Promise.all(uploadPromises).then(downloadURLs => {
            const newQuestions = [...questions];
            const currentQuestion = newQuestions[qIndex];
    
            if (isMultiple) {
                const existingImages = Array.isArray(currentQuestion[field]) ? currentQuestion[field] : (currentQuestion[field] ? [currentQuestion[field]] : []);
                currentQuestion[field] = [...existingImages, ...downloadURLs];
            } else {
                currentQuestion[field] = downloadURLs[0];
            }
    
            currentQuestion.isProcessing = false;
            setQuestions(newQuestions);
        }).catch(error => {
            console.error(`${field} upload failed:`, error);
            setModal({ show: true, message: `Image upload for ${field} failed: ${error.message}` });
            handleQuestionChange(qIndex, 'isProcessing', false);
        });
    };
    
    const handleImageReorder = (qIndex, field = 'images') => {
        const newQuestions = [...questions];
        let currentImages = Array.isArray(newQuestions[qIndex][field]) 
            ? [...newQuestions[qIndex][field]] 
            : [newQuestions[qIndex][field]].filter(Boolean);

        const draggedItemContent = currentImages.splice(dragItem.current, 1)[0];
        currentImages.splice(dragOverItem.current, 0, draggedItemContent);
        
        newQuestions[qIndex][field] = currentImages;
        dragItem.current = null;
        dragOverItem.current = null;
        setQuestions(newQuestions);
    };

    const removeImage = (qIndex, imgIndex, field = 'images') => {
        const newQuestions = [...questions];
        let currentImages = Array.isArray(newQuestions[qIndex][field]) 
            ? [...newQuestions[qIndex][field]] 
            : [newQuestions[qIndex][field]].filter(Boolean);

        currentImages.splice(imgIndex, 1);
        newQuestions[qIndex][field] = currentImages;
        setQuestions(newQuestions);
    };

    const handleExplanationImageUpload = async (event, qIndex) => {
        const file = event.target.files[0];
        if (!file) return;
        handleQuestionChange(qIndex, 'isProcessing', true);
        try {
            const storageRef = ref(storage, `explanation-images/${Date.now()}-${file.name}`);
            const snapshot = await uploadBytes(storageRef, file);
            const downloadURL = await getDownloadURL(snapshot.ref);
            const newQuestions = [...questions];
            newQuestions[qIndex].explanation = downloadURL;
            newQuestions[qIndex].isProcessing = false;
            setQuestions(newQuestions);
        } catch (error) {
            console.error("Explanation image upload failed:", error);
            setModal({ show: true, message: `Explanation image upload failed: ${error.message}` });
            handleQuestionChange(qIndex, 'isProcessing', false);
        }
    };

    const handleFinish = async (e) => {
        e.preventDefault();

        for (let i = 0; i < questions.length; i++) {
            const q = questions[i];
            if (q.type === 'mcq') {
                if (!q.correctAnswer || q.correctAnswer.length === 0) {
                    setModal({ show: true, message: `Please select at least one correct answer for Question ${i + 1} (MCQ).` });
                    return; 
                }
            }
        }
        
        setIsLoading(true);
        const savedExamId = await saveExamToFirestore(false);
        setIsLoading(false);
        if (savedExamId) onFinishEditing();
    };

    return (
        <div className="bg-white p-6 rounded-lg shadow mt-8">
            {/* -- MODALS -- */}
            {showBankSelector && (
                <BankSelectorModal 
                    paperId={paper.id} 
                    onImport={handleBankImport} 
                    onClose={() => setShowBankSelector(false)} 
                    setModal={setModal}
                />
            )}
            {showAutoGenerator && (
                <AutoGeneratorModal 
                    paperId={paper.id} 
                    onGenerate={handleAutoGenerate} 
                    onClose={() => setShowAutoGenerator(false)} 
                    setModal={setModal}
                />
            )}

            <div className="flex justify-between items-center mb-4">
                <h3 className="text-2xl font-bold text-gray-800">{examToEdit ? 'Edit Exam' : 'Create New Exam'} for {paper.name}</h3>
                <div className="flex items-center gap-2">
                    {saveStatus === 'saving' && <span className="text-sm text-gray-500 italic">Saving draft...</span>}
                    {saveStatus === 'saved' && <span className="text-sm text-green-600 font-semibold">Draft saved</span>}
                    {saveStatus === 'error' && <span className="text-sm text-red-500 font-semibold">Error saving!</span>}
                </div>
            </div>

            <form onSubmit={handleFinish} className="space-y-6">
                <div>
                    <label className="block text-gray-700 font-bold mb-2">Exam Title</label>
                    <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} className="w-full p-2 border rounded" required />
                </div>
                
                <div className="p-4 rounded-lg border border-purple-200 bg-purple-50">
                    <label htmlFor="exam-category" className="block text-gray-700 font-bold mb-2">Exam category</label>
                    <select id="exam-category" value={category} onChange={event => setCategory(event.target.value)} className="w-full p-2 border rounded bg-white">
                        <option value="auto">Automatic from title</option>
                        {EXAM_CATEGORIES.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}
                    </select>
                    <p className="text-sm text-purple-800 mt-2">{category === 'auto' ? `Current title category: ${examCategoryLabel(inferExamCategory(title))}. ` : ''}Students can filter by this category. Create a version and make it active to publish changes.</p>
                </div>

                <div className="flex gap-4">
                    <div className="w-1/2">
                        <label className="block text-gray-700 font-bold mb-2">Duration (minutes)</label>
                        <input type="number" value={duration} onChange={(e) => setDuration(e.target.value)} className="w-full p-2 border rounded" required />
                    </div>
                    <div className="w-1/2">
                        <label className="block text-gray-700 font-bold mb-2">Total Marks</label>
                        <div className="w-full p-2 border rounded bg-purple-50 text-purple-800 font-bold flex items-center">
                            {totalExamMarks}
                        </div>
                    </div>
                </div>
                
                <div>
                    <label className="block text-gray-700 font-bold mb-2">Exam Mode</label>
                    <div className="flex gap-4">
                        <label className="flex items-center p-3 border rounded-lg cursor-pointer">
                            <input
                                type="radio"
                                name="examMode"
                                value="on-demand"
                                checked={mode === 'on-demand'}
                                onChange={(e) => setMode(e.target.value)}
                                className="h-4 w-4 text-purple-600"
                            />
                            <span className="ml-2">On-Demand (Self-Paced)</span>
                        </label>
                        <label className="flex items-center p-3 border rounded-lg cursor-pointer">
                            <input
                                type="radio"
                                name="examMode"
                                value="invigilated"
                                checked={mode === 'invigilated'}
                                onChange={(e) => setMode(e.target.value)}
                                className="h-4 w-4 text-purple-600"
                            />
                            <span className="ml-2">Invigilated (Admin Start)</span>
                        </label>
                    </div>
                </div>

                {/* --- VERSION MANAGEMENT SECTION --- */}
                {examId && (
                    <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg space-y-4">
                        <div className="flex justify-between items-center">
                            <div>
                                <h4 className="font-bold text-indigo-900">Version History & Publishing</h4>
                                <p className="text-xs text-indigo-700">Create versions to snapshot your changes. Select an active version for students to see.</p>
                            </div>
                            <button
                                type="button"
                                onClick={handleCreateVersion}
                                disabled={isCreatingVersion || saveStatus === 'saving'}
                                className="px-4 py-2 bg-indigo-600 text-white text-sm font-bold rounded hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2 transition-colors"
                            >
                                {isCreatingVersion ? <Spinner size="small" /> : '💾 Save as New Version'}
                            </button>
                        </div>

                        {/* Manual Versions */}
                        {versions.length > 0 ? (
                            <div className="bg-white rounded border border-indigo-100 overflow-hidden mb-4">
                                <table className="w-full text-sm text-left">
                                    <thead className="bg-indigo-100 text-indigo-800 font-semibold">
                                        <tr>
                                            <th className="px-4 py-2">Version</th>
                                            <th className="px-4 py-2">Date</th>
                                            <th className="px-4 py-2">Status</th>
                                            <th className="px-4 py-2 text-right">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {versions.map((v) => (
                                            <tr key={v.id} className="border-t border-indigo-50 hover:bg-indigo-50 transition-colors">
                                                <td className="px-4 py-3 font-medium text-gray-800">Version {v.versionNumber}</td>
                                                <td className="px-4 py-3 text-gray-600">{new Date(v.createdAt).toLocaleString()}</td>
                                                <td className="px-4 py-3">
                                                    {activeVersionId === v.id ? (
                                                        <span className="bg-green-100 text-green-800 text-xs font-bold px-2 py-1 rounded-full">Active for Students</span>
                                                    ) : (
                                                        <span className="text-gray-400 text-xs italic">Archived</span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 text-right space-x-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRestoreVersion(v, false)}
                                                        className="text-blue-600 hover:text-blue-800 font-medium text-xs px-2"
                                                    >
                                                        Restore to Editor
                                                    </button>
                                                    {activeVersionId !== v.id && (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleSetActiveVersion(v)}
                                                            className="bg-green-500 hover:bg-green-600 text-white font-medium text-xs px-3 py-1 rounded transition-colors"
                                                        >
                                                            Make Active
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="text-center py-4 bg-white rounded border border-indigo-100 text-sm text-gray-500 mb-4">
                                No versions created yet. The exam is currently a draft. Save a new version to publish it!
                            </div>
                        )}

                        {/* Recent Auto-Saves (Last 10) - CLEARLY VISIBLE ADMIN SELECTOR */}
                        {autoSaves.length > 0 && (
                            <div className="mt-4 border-t border-indigo-200 pt-4">
                                <h4 className="font-bold text-sm text-indigo-800 mb-2">Recent Auto-Saves (Click to load an earlier draft)</h4>
                                <div className="bg-white rounded border border-indigo-100 overflow-hidden max-h-48 overflow-y-auto shadow-inner">
                                    <table className="w-full text-xs text-left">
                                        <thead className="bg-gray-100 text-gray-700 sticky top-0">
                                            <tr>
                                                <th className="px-4 py-2 border-b">Saved At</th>
                                                <th className="px-4 py-2 border-b text-right">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {[...autoSaves].reverse().map((save) => (
                                                <tr key={save.id} className="border-b border-gray-50 hover:bg-indigo-50 transition-colors">
                                                    <td className="px-4 py-3 text-gray-700 font-medium">
                                                        {new Date(save.timestamp).toLocaleString()}
                                                    </td>
                                                    <td className="px-4 py-3 text-right">
                                                        <button
                                                            type="button"
                                                            onClick={() => handleRestoreVersion(save, true)}
                                                            className="text-blue-600 hover:text-blue-800 font-bold bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded transition-colors"
                                                        >
                                                            Restore this Draft
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                )}
                {/* ------------------------------------------- */}
                
                <div className="flex gap-4 p-4 bg-gray-50 border rounded-lg">
                    <div className="flex-1">
                        <h4 className="font-bold text-gray-700 mb-2">Populate Exam</h4>
                        <div className="flex gap-3">
                            <button 
                                type="button" 
                                onClick={() => setShowBankSelector(true)} 
                                className="flex items-center gap-2 bg-blue-100 text-blue-800 px-4 py-2 rounded font-semibold hover:bg-blue-200"
                            >
                                <span>📥</span> Select from Bank
                            </button>
                            <button 
                                type="button" 
                                onClick={() => setShowAutoGenerator(true)} 
                                className="flex items-center gap-2 bg-pink-100 text-pink-800 px-4 py-2 rounded font-semibold hover:bg-pink-200"
                            >
                                <span>⚡</span> Auto-Generate
                            </button>
                        </div>
                    </div>
                </div>

                {questions.map((q, qIndex) => (
                    <div 
                        key={qIndex} 
                        className="p-4 border rounded-lg bg-gray-50 space-y-3 group transition-colors hover:bg-gray-100"
                        draggable
                        onDragStart={() => dragQuestionItem.current = qIndex}
                        onDragEnter={() => dragOverQuestionItem.current = qIndex}
                        onDragEnd={handleQuestionReorder}
                        onDragOver={(e) => e.preventDefault()}
                    >
                        <div className="flex justify-between items-center">
                            <div className="flex items-center gap-2">
                                <div className="cursor-grab text-gray-400 hover:text-gray-600" title="Drag to reorder">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="9" cy="12" r="1"></circle>
                                        <circle cx="9" cy="5" r="1"></circle>
                                        <circle cx="9" cy="19" r="1"></circle>
                                        <circle cx="15" cy="12" r="1"></circle>
                                        <circle cx="15" cy="5" r="1"></circle>
                                        <circle cx="15" cy="19" r="1"></circle>
                                    </svg>
                                </div>
                                <h4 className="font-semibold text-lg">Question {qIndex + 1} ({q.type.replace('-', ' ').toUpperCase()})</h4>
                                {q.createdAt && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Bank</span>}
                            </div>
                            <div className="flex flex-wrap items-center gap-4">
                                
                                <div>
                                    <label className="block text-purple-600 text-[10px] font-bold mb-1 text-right">Syllabus Topic (Required for Report)</label>
                                    <select
                                        value={q.syllabusArea || ''}
                                        onChange={(e) => handleQuestionChange(qIndex, 'syllabusArea', e.target.value)}
                                        className="w-48 p-1 border border-purple-200 rounded bg-purple-50 text-purple-800 text-xs font-semibold focus:ring-2 focus:ring-purple-500"
                                        required
                                    >
                                        <option value="">Select Topic Area...</option>
                                        {paper.sections?.map((sec, idx) => (
                                            <option key={idx} value={sec.topic || sec.name}>
                                                {sec.topic || sec.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {paper.sections && paper.sections.length > 0 ? (
                                    <div>
                                        <label className="block text-gray-600 text-[10px] font-bold mb-1 text-right">Paper Section</label>
                                        <select
                                            value={q.section}
                                            onChange={(e) => handleQuestionChange(qIndex, 'section', e.target.value)}
                                            className="w-28 p-1 border rounded text-xs"
                                        >
                                            {paper.sections.map((sec, idx) => (
                                                <option key={idx} value={sec.name}>{sec.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                ) : (
                                    <div>
                                         <label className="block text-gray-600 text-[10px] font-bold mb-1 text-right">Paper Section</label>
                                         <select value={q.section} onChange={(e) => handleQuestionChange(qIndex, 'section', e.target.value)} className="w-20 p-1 border rounded text-xs">
                                            <option value="A">A</option>
                                            <option value="B">B</option>
                                            <option value="C">C</option>
                                         </select>
                                    </div>
                                )}
                                <div>
                                    <label className="block text-gray-600 text-[10px] font-bold mb-1 text-right">Marks</label>
                                    <input
                                        type="number"
                                        value={q.marks || ''}
                                        onChange={(e) => handleQuestionChange(qIndex, 'marks', e.target.value === '' ? '' : Number(e.target.value))}
                                        className="w-20 p-1 border rounded text-center text-xs font-bold"
                                        required
                                        min="0"
                                    />
                                </div>
                                <button type="button" onClick={() => setQuestions(qs => qs.filter((_, i) => i !== qIndex))} className="text-red-500 hover:text-red-700 text-xs font-bold self-end pb-1">Remove</button>
                            </div>
                        </div>
                        <div>
                            <label className="block text-gray-700 font-bold mb-2">Question Text</label>
                            
                            <div className="bg-white border rounded min-h-[150px] overflow-hidden mb-4">
                                <AdvancedWordProcessor 
                                    value={q.text || ''} 
                                    onChange={(val) => handleQuestionChange(qIndex, 'text', val)} 
                                />
                            </div>
                            
                            <div className="mt-4">
                                <label className="block text-gray-700 font-bold mb-2">Question Images (Optional)</label>
                                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 mt-2">
                                    {Array.isArray(q.images) && q.images.map((imgSrc, imgIndex) => (
                                        <div key={imgIndex} className="relative group border-2 border-dashed rounded-lg p-1 cursor-grab" draggable onDragStart={(e) => { e.stopPropagation(); dragItem.current = imgIndex; }} onDragEnter={(e) => { e.stopPropagation(); dragOverItem.current = imgIndex; }} onDragEnd={(e) => { e.stopPropagation(); handleImageReorder(qIndex, 'images'); }} onDragOver={(e) => e.preventDefault()}>
                                            <img src={imgSrc} alt={`Question part ${imgIndex + 1}`} className="w-full h-24 object-contain rounded"/>
                                            <button type="button" onClick={() => removeImage(qIndex, imgIndex, 'images')} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full h-5 w-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity">&times;</button>
                                        </div>
                                    ))}
                                </div>
                                <label htmlFor={`q-img-upload-${qIndex}`} className="mt-2 cursor-pointer bg-white py-2 px-3 border border-gray-300 rounded-md shadow-sm text-sm leading-4 font-medium text-gray-700 hover:bg-gray-50 inline-block">
                                    {q.isProcessing ? 'Processing...' : 'Upload Image(s)'}
                                </label>
                                <input id={`q-img-upload-${qIndex}`} type="file" className="hidden" onChange={(e) => handleImageUpload(e, qIndex, 'images', true)} accept="image/*" multiple />
                                {q.isProcessing && <div className="mt-2 text-sm text-gray-500">Processing image(s)...</div>}
                            </div>
                        </div>
                        
                        {q.type === 'mcq' && (
                            <>
                                <h5 className="font-semibold text-gray-700 pt-2">Options (Select all correct answers)</h5>
                                {q.options.map((opt, oIndex) => (
                                    <div key={oIndex} className="flex items-center gap-3">
                                        <input type="checkbox" checked={(q.correctAnswer || []).includes(oIndex)} onChange={() => handleCorrectAnswerChange(qIndex, oIndex)} className="h-4 w-4 rounded"/>
                                        <input type="text" value={opt} onChange={(e) => handleOptionChange(qIndex, oIndex, e.target.value)} className="w-full p-2 border rounded" placeholder={`Option ${oIndex + 1}`} required />
                                    </div>
                                ))}
                            </>
                        )}

                        {q.type === 'dropdown' && (
                            <>
                                <h5 className="font-semibold text-gray-700 pt-2">Options (Select the correct answer)</h5>
                                {q.options.map((opt, oIndex) => (
                                    <div key={oIndex} className="flex items-center gap-3">
                                        <input type="radio" name={`correctAnswer-${qIndex}`} checked={q.correctAnswer === oIndex} onChange={() => handleQuestionChange(qIndex, 'correctAnswer', oIndex)} />
                                        <input type="text" value={opt} onChange={(e) => handleOptionChange(qIndex, oIndex, e.target.value)} className="w-full p-2 border rounded" placeholder={`Option ${oIndex + 1}`} required />
                                    </div>
                                ))}
                            </>
                        )}
                        
                        {q.type === 'short-answer' && (
                            <div>
                                <h5 className="font-semibold text-gray-700 pt-2">Correct Answer</h5>
                                <p className="text-xs text-gray-500 mb-1">Student's answer must be an exact match (case-insensitive).</p>
                                <input 
                                    type="text" 
                                    value={q.correctAnswer || ''}
                                    onChange={(e) => handleQuestionChange(qIndex, 'correctAnswer', e.target.value)}
                                    className="w-full p-2 border rounded"
                                    placeholder="Enter the exact correct answer"
                                    required
                                />
                            </div>
                        )}
                        
                        {q.type === 'matching' && (
                            <div className="grid grid-cols-2 gap-6">
                                <div>
                                    <h5 className="font-semibold text-gray-700">Prompts</h5>
                                    {q.prompts.map((prompt, pIndex) => (
                                        <div key={pIndex} className="flex items-center gap-2 mt-1">
                                            <input type="text" value={prompt} onChange={(e) => handleMatchingChange(qIndex, 'prompts', pIndex, e.target.value)} className="w-full p-2 border rounded" placeholder={`Prompt ${pIndex + 1}`} />
                                            <button type="button" onClick={() => removeMatchingItem(qIndex, 'prompts', pIndex)} className="text-red-500 hover:text-red-700">&times;</button>
                                        </div>
                                    ))}
                                    <button type="button" onClick={() => addMatchingItem(qIndex, 'prompts')} className="text-sm text-purple-600 hover:underline mt-1">Add Prompt</button>
                                </div>
                                <div>
                                    <h5 className="font-semibold text-gray-700">Options</h5>
                                    {q.options.map((option, oIndex) => (
                                        <div key={oIndex} className="flex items-center gap-2 mt-1">
                                            <input type="text" value={option} onChange={(e) => handleMatchingChange(qIndex, 'options', oIndex, e.target.value)} className="w-full p-2 border rounded" placeholder={`Option ${oIndex + 1}`} />
                                            <button type="button" onClick={() => removeMatchingItem(qIndex, 'options', oIndex)} className="text-red-500 hover:text-red-700">&times;</button>
                                        </div>
                                    ))}
                                    <button type="button" onClick={() => addMatchingItem(qIndex, 'options')} className="text-sm text-purple-600 hover:underline mt-1">Add Option</button>
                                </div>
                                <div className="col-span-2">
                                     <h5 className="font-semibold text-gray-700">Correct Pairs</h5>
                                     {q.prompts.map((prompt, pIndex) => (
                                         <div key={pIndex} className="flex items-center gap-4 mt-2 p-2 bg-gray-100 rounded">
                                            <span className="flex-1 font-medium">{prompt || `Prompt ${pIndex + 1}`}</span>
                                            <span>&#8594;</span>
                                            <select 
                                                value={q.correctAnswer[pIndex] ?? ''}
                                                onChange={(e) => handleMatchingCorrectAnswerChange(qIndex, pIndex, e.target.value)}
                                                className="flex-1 p-2 border rounded bg-white"
                                            >
                                                <option value="">Select a match...</option>
                                                {q.options.map((option, oIndex) => (
                                                    <option key={oIndex} value={oIndex}>{option || `Option ${oIndex + 1}`}</option>
                                                ))}
                                            </select>
                                         </div>
                                     ))}
                                </div>
                            </div>
                        )}

                        {q.type === 'mcq-grid' && ( <div className="space-y-4"> {/* Placeholder as in original file */} </div> )}

                        {q.type === 'descriptive' && (
                            <div className="space-y-4 pt-4 border-t">
                                <StarterSpreadsheetEditor value={q.starterSpreadsheet} onChange={value=>handleQuestionChange(qIndex,'starterSpreadsheet',value)} marks={q.marks} onMarksChange={value=>handleQuestionChange(qIndex,'marks',value)}/>
                                <div>
                                    <h5 className="font-semibold text-gray-700">Suggested Answer</h5>
                                    <p className="text-xs text-gray-500 mb-2">You can provide a Word/Image based answer and/or a Spreadsheet answer. Both will be used for AI evaluation.</p>
                                    
                                    <div className="flex gap-2 mb-2 border-b">
                                        <button 
                                            type="button" 
                                            onClick={() => handleQuestionChange(qIndex, 'suggestedAnswerView', 'main')} 
                                            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${!q.suggestedAnswerView || q.suggestedAnswerView === 'main' ? 'bg-white border-t border-l border-r border-gray-300 text-purple-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                        >
                                            Main Answer (Word/Image)
                                        </button>
                                        <button 
                                            type="button" 
                                            onClick={() => handleQuestionChange(qIndex, 'suggestedAnswerView', 'excel')} 
                                            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${q.suggestedAnswerView === 'excel' ? 'bg-white border-t border-l border-r border-gray-300 text-purple-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                        >
                                            Spreadsheet Answer
                                        </button>
                                    </div>

                                    {(!q.suggestedAnswerView || q.suggestedAnswerView === 'main') && (
                                        <div className="bg-white p-2 rounded-b-lg border border-t-0 border-gray-300">
                                            <div className="flex items-center gap-2 mb-2">
                                                <span className="text-sm font-medium text-gray-600">Format:</span>
                                                <button type="button" onClick={() => handleQuestionChange(qIndex, 'suggestedAnswerFormat', 'text')} className={`px-3 py-1 text-sm rounded-md ${q.suggestedAnswerFormat === 'text' ? 'bg-purple-600 text-white' : 'bg-gray-200'}`}>Text</button>
                                                <button type="button" onClick={() => handleQuestionChange(qIndex, 'suggestedAnswerFormat', 'image')} className={`px-3 py-1 text-sm rounded-md ${q.suggestedAnswerFormat === 'image' ? 'bg-purple-600 text-white' : 'bg-gray-200'}`}>Image</button>
                                            </div>

                                            {q.suggestedAnswerFormat === 'image' ? (
                                                <div>
                                                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 mt-2 mb-2">
                                                        {(Array.isArray(q.suggestedAnswerContent) ? q.suggestedAnswerContent : (q.suggestedAnswerContent ? [q.suggestedAnswerContent] : [])).map((imgSrc, imgIndex) => (
                                                            <div 
                                                                key={imgIndex} 
                                                                className="relative group border-2 border-dashed rounded-lg p-1 cursor-grab" 
                                                                draggable 
                                                                onDragStart={(e) => { e.stopPropagation(); dragItem.current = imgIndex; }} 
                                                                onDragEnter={(e) => { e.stopPropagation(); dragOverItem.current = imgIndex; }} 
                                                                onDragEnd={(e) => { e.stopPropagation(); handleImageReorder(qIndex, 'suggestedAnswerContent'); }} 
                                                                onDragOver={(e) => e.preventDefault()}
                                                            >
                                                                <img src={imgSrc} alt={`Suggested Answer ${imgIndex + 1}`} className="w-full h-24 object-contain rounded"/>
                                                                <button 
                                                                    type="button" 
                                                                    onClick={() => removeImage(qIndex, imgIndex, 'suggestedAnswerContent')} 
                                                                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full h-5 w-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                                                                >
                                                                    &times;
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                    <label htmlFor={`sa-upload-${qIndex}`} className="cursor-pointer bg-white py-2 px-3 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 inline-block">
                                                        {q.isProcessing ? 'Processing...' : 'Upload Image(s)'}
                                                    </label>
                                                    <input id={`sa-upload-${qIndex}`} type="file" className="hidden" onChange={(e) => handleImageUpload(e, qIndex, 'suggestedAnswerContent', true)} accept="image/*" multiple />
                                                </div>
                                            ) : (
                                                <div className="h-[500px] border rounded overflow-hidden">
                                                    <AdvancedWordProcessor 
                                                        key={`sa-editor-${qIndex}`} 
                                                        value={q.suggestedAnswerContent || ''} 
                                                        onChange={(content) => handleQuestionChange(qIndex, 'suggestedAnswerContent', content)} 
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {q.suggestedAnswerView === 'excel' && (
                                        <div className="bg-white p-2 rounded-b-lg border border-t-0 border-gray-300 h-[500px]">
                                            <CustomSpreadsheet 
                                                key={`sa-excel-${qIndex}`}
                                                data={q.suggestedAnswerExcel} 
                                                onChange={(data) => handleQuestionChange(qIndex, 'suggestedAnswerExcel', data)} 
                                            />
                                        </div>
                                    )}
                                </div>
                                
                                <div>
                                    <h5 className="font-semibold text-gray-700">Marking Scheme</h5>
                                    <div className="flex items-center gap-2 my-2">
                                        <span className="text-sm font-medium text-gray-600">Format:</span>
                                        <button type="button" onClick={() => handleQuestionChange(qIndex, 'markingSchemeFormat', 'text')} className={`px-3 py-1 text-sm rounded-md ${q.markingSchemeFormat === 'text' ? 'bg-purple-600 text-white' : 'bg-gray-200'}`}>Text</button>
                                        <button type="button" onClick={() => handleQuestionChange(qIndex, 'markingSchemeFormat', 'image')} className={`px-3 py-1 text-sm rounded-md ${q.markingSchemeFormat === 'image' ? 'bg-purple-600 text-white' : 'bg-gray-200'}`}>Image</button>
                                    </div>
                                    {q.markingSchemeFormat === 'image' ? (
                                        <div>
                                            {q.markingSchemeContent && <img src={q.markingSchemeContent} alt="Marking Scheme" className="border rounded-lg h-24 w-auto my-2"/>}
                                            <label htmlFor={`ms-upload-${qIndex}`} className="cursor-pointer bg-white py-2 px-3 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50">Upload Image</label>
                                            <input id={`ms-upload-${qIndex}`} type="file" className="hidden" onChange={(e) => handleImageUpload(e, qIndex, 'markingSchemeContent', false)} accept="image/*" />
                                        </div>
                                    ) : (
                                        <div className="h-[400px] border rounded overflow-hidden">
                                            <AdvancedWordProcessor 
                                                key={`ms-editor-${qIndex}`} 
                                                value={q.markingSchemeContent || ''} 
                                                onChange={(content) => handleQuestionChange(qIndex, 'markingSchemeContent', content)} 
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        {['mcq', 'dropdown', 'mcq-grid', 'short-answer', 'matching'].includes(q.type) && (
                            <div className="mt-4 pt-4 border-t border-gray-300">
                                <h5 className="font-semibold text-gray-700">Answer Explanation</h5>
                                <div className="flex items-center gap-2 my-2">
                                    <span className="text-sm font-medium text-gray-600">Format:</span>
                                    <button type="button" onClick={() => handleQuestionChange(qIndex, 'explanationFormat', 'text')} className={`px-3 py-1 text-sm rounded-md ${q.explanationFormat === 'text' || !q.explanationFormat ? 'bg-purple-600 text-white' : 'bg-gray-200'}`}>Text</button>
                                    <button type="button" onClick={() => handleQuestionChange(qIndex, 'explanationFormat', 'image')} className={`px-3 py-1 text-sm rounded-md ${q.explanationFormat === 'image' ? 'bg-purple-600 text-white' : 'bg-gray-200'}`}>Image</button>
                                </div>
                                {q.explanationFormat === 'image' ? (
                                    <div>
                                        {q.explanation && (
                                            <div className="relative w-48 my-2"><img src={q.explanation} alt="Explanation" className="border rounded-lg object-contain h-24 w-48"/><button type="button" onClick={() => handleQuestionChange(qIndex, 'explanation', '')} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full h-5 w-5 flex items-center justify-center text-xs">&times;</button></div>
                                        )}
                                        <label htmlFor={`exp-upload-${qIndex}`} className="cursor-pointer bg-white py-2 px-3 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 inline-block">{q.isProcessing ? "Processing..." : "Upload Image"}</label>
                                        <input id={`exp-upload-${qIndex}`} type="file" className="hidden" onChange={(e) => handleExplanationImageUpload(e, qIndex)} accept="image/*" />
                                    </div>
                                ) : (
                                    <textarea value={q.explanation || ''} onChange={(e) => handleQuestionChange(qIndex, 'explanation', e.target.value)} className="w-full p-2 border rounded" placeholder="Provide a detailed explanation for the correct answer." rows="3"/>
                                )}
                            </div>
                        )}
                    </div>
                ))}
                
                <h4 className="font-bold text-gray-700 mt-6">Create New Question Manually</h4>
                <div className="flex flex-wrap gap-4">
                    {!isProfessional && (
                        <>
                            <button type="button" onClick={() => addQuestion('mcq')} className="bg-purple-200 text-purple-800 font-bold py-2 px-4 rounded hover:bg-purple-300">Add MCQ</button>
                            <button type="button" onClick={() => addQuestion('dropdown')} className="bg-yellow-200 text-yellow-800 font-bold py-2 px-4 rounded hover:bg-yellow-300">Add Drop Down</button>
                            <button type="button" onClick={() => addQuestion('short-answer')} className="bg-blue-200 text-blue-800 font-bold py-2 px-4 rounded hover:bg-blue-300">Add Short Answer</button>
                            <button type="button" onClick={() => addQuestion('matching')} className="bg-pink-200 text-pink-800 font-bold py-2 px-4 rounded hover:bg-pink-300">Add Matching</button>
                            <button type="button" onClick={() => addQuestion('mcq-grid')} className="bg-indigo-200 text-indigo-800 font-bold py-2 px-4 rounded hover:bg-indigo-300">Add MC Grid</button>
                        </>
                    )}
                    <button type="button" onClick={() => addQuestion('descriptive')} className="bg-green-200 text-green-800 font-bold py-2 px-4 rounded hover:bg-green-300">Add Descriptive</button>
                </div>
                <div className="flex gap-4">
                    <button type="submit" disabled={isLoading} className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold py-3 px-4 rounded-lg hover:from-purple-700 hover:to-pink-700 transition disabled:from-purple-300 disabled:to-pink-300 flex items-center justify-center h-12">
                        {isLoading ? <Spinner /> : 'Finish Editing'}
                    </button>
                    <button type="button" onClick={onFinishEditing} className="w-full bg-gray-200 text-gray-800 font-bold py-3 px-4 rounded-lg hover:bg-gray-300">Go Back</button>
                </div>
            </form>
        </div>
    );
};
export default CreateExam;